import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthProvider';
import Card from '../../components/Card';
import Button from '../../components/Button';

/* ───────── layout ───────── */
const Wrap = styled.div`
  padding: 1.25rem;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;

  /* Desktop: emphasize the grid; keep dropzone in a secondary column */
  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) 320px;
    align-items: start;
  }
`;

/* Main Assets card (grid + tabs) */
const AssetsCard = styled(Card)`
  padding: 1.25rem;
`;

/* Upload card (drag & drop) */
const UploadCard = styled(Card)`
  padding: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 .25rem;
  font-size: clamp(1.25rem, 2.2vw, 1.6rem);
  color: #E8EAED;
`;

const SectionSub = styled.p`
  margin: 0 0 .75rem;
  color: #9AA4B2;
`;

/* ───────── tabs (prominent, like AuthModal tabs) ───────── */
const Tabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin: .25rem 0 1rem;
`;

const TabBtn = styled.button<{ active?: boolean }>`
  border-radius: 12px;
  padding: .75rem .9rem;
  border: 1px solid ${({ active }) => (active ? 'rgba(199,210,254,.5)' : 'rgba(255,255,255,.08)')};
  background: ${({ active }) => (active ? 'rgba(199,210,254,.10)' : 'rgba(255,255,255,.04)')};
  color: ${({ active }) => (active ? '#E8EAED' : '#cbd2e1')};
  font-weight: 800;
  letter-spacing: .2px;
  text-transform: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  gap: .5rem;
  transition: background .2s ease, border-color .2s ease, color .2s ease, transform .06s ease;
  &:hover { background: rgba(255,255,255,.07); }
`;

const CountPill = styled.span`
  font-size: .8rem;
  font-weight: 800;
  line-height: 1;
  padding: .25rem .5rem;
  border-radius: 999px;
  background: rgba(255,255,255,.08);
  color: #E8EAED;
`;

/* ───────── toolbar (optional future: search/sort) ───────── */
const Toolbar = styled.div`
  display: flex;
  gap: .5rem;
  align-items: center;
  justify-content: flex-end;
  margin: -.25rem 0 .5rem;
`;

/* ───────── asset grid ───────── */
const Grid = styled.div`
  display: grid;
  gap: .75rem;

  /* Mobile-first sizing; grows naturally */
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));

  @media (min-width: 520px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  }
  @media (min-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const Tile = styled.div`
  position: relative;
  border-radius: 12px;
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(255,255,255,.04);
  color: #E8EAED;
  padding: .75rem;
  display: grid;
  gap: .5rem;
  transition: transform .06s ease, background .2s ease, border-color .2s ease;
  &:hover { transform: translateY(-1px); background: rgba(255,255,255,.06); }
`;

const Thumb = styled.div<{ tone?: 'blue'|'purple'|'amber'|'teal'|'gray' }>`
  height: 96px; border-radius: 8px;
  background:
    radial-gradient(140px 80px at 20% -20%, rgba(255,255,255,.14), rgba(255,255,255,0) 70%),
    ${({ tone }) => (
      tone === 'blue'   ? 'linear-gradient(135deg,#5EA2EF33,#3B82F633)'
    : tone === 'purple' ? 'linear-gradient(135deg,#A78BFA33,#7C3AED33)'
    : tone === 'amber'  ? 'linear-gradient(135deg,#FCD34D33,#F59E0B33)'
    : tone === 'teal'   ? 'linear-gradient(135deg,#5EEAD433,#14B8A633)'
    : 'linear-gradient(135deg,#CBD5E133,#64748B33)')};
  border: 1px solid rgba(255,255,255,.06);
`;

const Name = styled.div`
  font-weight: 800; font-size: .95rem; line-height: 1.2;
  color: #E8EAED;
  word-break: break-word;
`;

const Meta = styled.div`
  display: flex; align-items: center; gap: .5rem;
  color: #cbd2e1; font-size: .85rem;
`;

const TypeBadge = styled.span`
  border-radius: 999px;
  padding: .1rem .5rem;
  border: 1px solid rgba(255,255,255,.12);
  background: rgba(255,255,255,.06);
  color: #E8EAED;
  font-weight: 700; font-size: .7rem; letter-spacing: .25px;
`;

/* ───────── dropzone card internals ───────── */
const DropBox = styled.div<{ $active?: boolean; $disabled?: boolean }>`
  border: 2px dashed ${({ $active }) => ($active ? 'rgba(199,210,254,.6)' : 'rgba(255,255,255,.18)')};
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  color: #cbd2e1;
  background: ${({ $active }) => ($active ? 'rgba(199,210,254,.08)' : 'rgba(255,255,255,.03)')};
  opacity: ${({ $disabled }) => ($disabled ? .6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: background .2s ease, border-color .2s ease;
`;

const DropNote = styled.p`
  margin: .25rem 0 0;
  color: #9AA4B2; font-size: .9rem;
`;

/* ───────── sample data (placeholder) ───────── */
type Asset = {
  id: string;
  name: string;
  size: string;
  type: 'pdf'|'zip'|'png'|'jpg'|'gif'|'docx'|'fig'|'link';
  tone?: 'blue'|'purple'|'amber'|'teal'|'gray';
};

const CLIENT_ASSETS: Asset[] = [
  { id:'c1', name:'Brand Guidelines.pdf', size:'2.3 MB', type:'pdf', tone:'purple' },
  { id:'c2', name:'Logo Mark.png',        size:'640 KB', type:'png', tone:'blue'   },
  { id:'c3', name:'Product Shots.zip',    size:'88 MB',  type:'zip', tone:'amber'  },
  { id:'c4', name:'Messaging.docx',       size:'210 KB', type:'docx', tone:'teal'  },
];

const AGENT_ASSETS: Asset[] = [
  { id:'a1', name:'Logo Pack (AI, EPS, SVG).zip', size:'12.4 MB', type:'zip', tone:'amber' },
  { id:'a2', name:'Social Templates.fig',        size:'1.8 MB',  type:'fig', tone:'purple' },
  { id:'a3', name:'Hero Banner.jpg',            size:'1.2 MB',  type:'jpg', tone:'blue'   },
];

/* ───────── page ───────── */
export default function AssetManagerPage() {
  const { isPaid } = useAuth();
  const [tab, setTab] = useState<'client' | 'agent'>('client');
  const [dragOver, setDragOver] = useState(false);

  const assets = useMemo(() => (tab === 'client' ? CLIENT_ASSETS : AGENT_ASSETS), [tab]);

  const onDrag = (e: React.DragEvent) => {
    e.preventDefault();
    if (isPaid) setDragOver(true);
  };
  const onLeave = () => setDragOver(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    // TODO: wire uploads; this is a styling-focused page
  };

  return (
    <Wrap>
      {/* MAIN: assets with prominent tabs */}
      <AssetsCard>
        <SectionTitle>Assets</SectionTitle>
        <SectionSub>
          Browse and download files. Use tabs to switch between <strong>client‑provided</strong> and <strong>agent‑delivered</strong>.
        </SectionSub>

        <Tabs role="tablist" aria-label="Asset type">
          <TabBtn
            role="tab"
            aria-selected={tab === 'client'}
            active={tab === 'client'}
            onClick={() => setTab('client')}
          >
            Client files <CountPill>{CLIENT_ASSETS.length}</CountPill>
          </TabBtn>
          <TabBtn
            role="tab"
            aria-selected={tab === 'agent'}
            active={tab === 'agent'}
            onClick={() => setTab('agent')}
          >
            Deliverables <CountPill>{AGENT_ASSETS.length}</CountPill>
          </TabBtn>
        </Tabs>

        <Toolbar>
          {/* Keep CTA available here too; you can replace this with search/sort later */}
          {isPaid ? (
            <Button onClick={() => document.getElementById('upload-dz')?.scrollIntoView({ behavior: 'smooth' })}>
              Upload files
            </Button>
          ) : (
            <Button onClick={() => (window.location.href = '/dashboard/billing')}>
              Upgrade to enable uploads
            </Button>
          )}
        </Toolbar>

        <Grid aria-live="polite">
          {assets.map(a => (
            <Tile key={a.id}>
              <Thumb tone={a.tone} />
              <Name title={a.name}>{a.name}</Name>
              <Meta>
                <TypeBadge>{a.type.toUpperCase()}</TypeBadge>
                <span>{a.size}</span>
              </Meta>
            </Tile>
          ))}
          {assets.length === 0 && (
            <div style={{ color:'#cbd2e1', padding:'.5rem 0' }}>No assets yet.</div>
          )}
        </Grid>
      </AssetsCard>

      {/* SIDEBAR: drag & drop upload */}
      <UploadCard id="upload-dz">
        <SectionTitle style={{ fontSize: '1.1rem', marginBottom: '.25rem' }}>Upload</SectionTitle>
        <SectionSub style={{ marginBottom: '.75rem' }}>
          Drag and drop files or use the button.
        </SectionSub>

        <DropBox
          $active={dragOver}
          $disabled={!isPaid}
          onDragOver={onDrag}
          onDragEnter={onDrag}
          onDragLeave={onLeave}
          onDrop={onDrop}
        >
          <div>Drag &amp; drop files here</div>
          {!isPaid && <DropNote>Uploads require an active subscription.</DropNote>}
        </DropBox>

        <div style={{ marginTop: 12 }}>
          {isPaid ? (
            <Button>Upload files</Button>
          ) : (
            <Button onClick={() => (window.location.href = '/dashboard/billing')}>
              Upgrade to enable uploads
            </Button>
          )}
        </div>
      </UploadCard>
    </Wrap>
  );
}
